import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Monitor, Mic, MicOff, Send, MessageSquare, X, Shield, Loader2 } from 'lucide-react';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CoViewingViewer({ session, adminToken, onClose }) {
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const replayerRef = useRef(null);
  const containerRef = useRef(null);
  const eventsBuffer = useRef([]);

  const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  useEffect(() => {
    const ws = new WebSocket(`${wsUrl}/api/ws/co-view/${session.room_id}?role=support`);
    wsRef.current = ws;
    ws.onopen = () => { setConnected(true); setupAudio(ws); };
    ws.onmessage = (evt) => {
      const msg = JSON.parse(evt.data);
      if (msg.type === 'rrweb_events' && msg.events) handleEvents(msg.events);
      if (msg.type === 'session_end') onClose();
      if (msg.type === 'chat_message') setChatMessages(p => [...p, { from: msg.from, message: msg.message, ts: Date.now() }]);
      if (msg.type === 'webrtc_offer') handleOffer(ws, msg.sdp);
      if (msg.type === 'webrtc_ice_candidate' && pcRef.current) pcRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate));
    };
    ws.onclose = () => setConnected(false);
    return () => { ws.close(); if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop()); if (pcRef.current) pcRef.current.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.room_id]);

  const handleEvents = async (events) => {
    eventsBuffer.current.push(...events);
    if (!replayerRef.current && containerRef.current && eventsBuffer.current.length > 0) {
      try {
        const { Replayer } = await import('rrweb');
        replayerRef.current = new Replayer(eventsBuffer.current, { root: containerRef.current, liveMode: true });
        replayerRef.current.startLive();
      } catch (e) { logger.error('Replayer init failed:', e); }
    } else if (replayerRef.current) {
      events.forEach(e => replayerRef.current.addEvent(e));
    }
  };

  const setupAudio = async (ws) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => { if (e.candidate && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'webrtc_ice_candidate', candidate: e.candidate })); };
      pc.ontrack = (e) => { const a = new Audio(); a.srcObject = e.streams[0]; a.play().catch(() => {}); };
    } catch (e) { logger.error('Audio setup failed:', e); }
  };

  const handleOffer = async (ws, sdp) => {
    if (!pcRef.current) return;
    await pcRef.current.setRemoteDescription(new RTCSessionDescription(sdp));
    const answer = await pcRef.current.createAnswer();
    await pcRef.current.setLocalDescription(answer);
    ws.send(JSON.stringify({ type: 'webrtc_answer', sdp: answer }));
  };

  const toggleMute = () => { if (localStreamRef.current) { localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; }); setMuted(!muted); } };
  const sendChat = () => { if (!chatInput.trim() || !wsRef.current) return; wsRef.current.send(JSON.stringify({ type: 'chat_message', message: chatInput })); setChatMessages(p => [...p, { from: 'support', message: chatInput, ts: Date.now() }]); setChatInput(''); };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col" data-testid="co-viewing-viewer">
      <div className="bg-slate-900 border-b border-slate-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Monitor className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-medium text-white">Co-View: {session.user_name || session.user_email}</span>
          <Badge className={`text-xs ${connected ? 'bg-emerald-600' : 'bg-red-600'}`}>{connected ? 'Connected' : 'Disconnected'}</Badge>
          {session.masked_mode && <Badge variant="outline" className="text-xs border-amber-500 text-amber-400"><Shield className="w-3 h-3 mr-1" /> Masked</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={toggleMute} className={muted ? 'text-red-400' : 'text-slate-300'}>
            {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="destructive" onClick={onClose}><X className="w-3.5 h-3.5 mr-1" /> Close</Button>
        </div>
      </div>
      <div className="flex-1 flex">
        <div className="flex-1 overflow-auto bg-slate-800" ref={containerRef}>
          {!connected && <div className="flex items-center justify-center h-full text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>}
        </div>
        <div className="w-64 bg-slate-900 border-l border-slate-700 flex flex-col">
          <div className="p-3 border-b border-slate-700"><span className="text-sm font-medium text-white flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Chat</span></div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chatMessages.map((msg, i) => (
              <div key={`msg-${msg.ts || i}`} className={`text-xs ${msg.from === 'support' ? 'text-emerald-400' : 'text-blue-400'}`}>
                <span className="font-medium">{msg.from === 'support' ? 'You' : 'User'}:</span> {msg.message}
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-700 flex gap-2">
            <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()}
              placeholder="Message..." className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-500" />
            <Button size="sm" variant="ghost" onClick={sendChat} className="text-emerald-400"><Send className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoViewingViewer;
